import { useCallback, useState } from 'react';
import type { CheckInResponseCreateInput } from '@/entities/check-in-response';
import type { Plan } from '@/entities/plan';
import type { Period } from '@/entities/types';
import { useSubmitCheckIn } from '@/features/plan/hooks/useCheckIn';
import { useCheckInInsights } from '@/features/plan/hooks/useCheckInInsights';

interface UseCheckInViewArgs {
  plan: Plan;
  period: Period;
  targetStartAt: Date;
  targetStartAtLocal: string;
}

export function useCheckInView({
  plan,
  period,
  targetStartAt,
  targetStartAtLocal,
}: UseCheckInViewArgs) {
  const [responses, setResponses] = useState<CheckInResponseCreateInput>({});
  const { submit, isPending, error } = useSubmitCheckIn();

  const { insights, resetInsights } = useCheckInInsights({
    period,
    targetStartAt,
    responses,
    isPaused: isPending,
  });

  const handleResponseChange = useCallback(
    (key: string, value: string) => {
      setResponses((prev) => {
        const next = { ...prev, [key]: value };
        const hasContent = Object.values(next).some(
          (response) => response !== '',
        );
        if (!hasContent) {
          resetInsights();
        }
        return next;
      });
    },
    [resetInsights],
  );

  const handleSubmit = useCallback(() => {
    void submit({
      planId: plan.id,
      period,
      startAtLocal: targetStartAtLocal,
      targetStartAt,
      responses,
      insights,
    });
  }, [
    submit,
    plan.id,
    period,
    targetStartAtLocal,
    targetStartAt,
    responses,
    insights,
  ]);

  return {
    responses,
    handleResponseChange,
    handleSubmit,
    isPending,
    error,
    insights,
    hasInsights: insights.length > 0,
  };
}
